import { Badge, BadgeProps } from '@chakra-ui/react'
import { forwardRef } from 'react'

import { StatusMap } from './statuses'

export interface IStatusBadgeProps extends Omit<BadgeProps, 'children'> {
  /** Valor cru que a API devolve (`'approved'`, `'PENDENTE'`). Resolvido pelo mapa em `statuses`. */
  status?: string | null
  /** Mapa de status do domínio, de `statuses.ts`. */
  statuses?: StatusMap
  /** Escape para o status que não tem mapa — um badge avulso de "Sim"/"Não". */
  label?: string
  /** Texto para o status desconhecido. Sem ele o valor cru aparece, que é o que ajuda a depurar. */
  fallbackLabel?: string
}

/**
 * Badge de status do painel.
 *
 * A cor e o rótulo vêm do mapa do domínio (`statuses.ts`), não da página — do mesmo jeito que o
 * `ConfirmDialog` tira a cópia do registry `ACTIONS`. Um status sem entrada no mapa sai cinza com
 * o valor cru, que é melhor do que sumir da tela.
 *
 * A **forma** (fonte, padding, raio) não é assunto nem daqui nem da página: mora no `theme.ts`.
 * Não passe `fontSize`, `px` nem `borderRadius`.
 */
/**
 * **Encaminha o ref** pelo mesmo motivo que o `<Icon>` e o `SectionCard`: o `<Tooltip>` clona o
 * filho e prende um ref nele para se posicionar, e um selo de status dentro de um tooltip é uma
 * das formas mais comuns do painel (o tempo de revisão da entrega de trabalho, o status de uma
 * candidatura). Sem isso o React avisava "Function components cannot be given refs" e o tooltip
 * não aparecia.
 */
const StatusBadge = forwardRef<HTMLSpanElement, IStatusBadgeProps>(function StatusBadge(
  { status, statuses, label, fallbackLabel, colorScheme, ...props },
  ref
) {
  const config = status && statuses ? statuses[status] : undefined
  const text = label ?? config?.label ?? fallbackLabel ?? status

  if (!text) return null

  // O mapa manda. Um `colorScheme` passado à mão só serve para o badge sem mapa nenhum —
  // se ele vencesse o registry, a cor voltaria a ser decisão da página.
  return (
    <Badge ref={ref} {...props} colorScheme={config?.colorScheme ?? colorScheme ?? 'gray'}>
      {text}
    </Badge>
  )
})

export default StatusBadge
